<template>
  <div class="terms-and-conditions rounded-lg m-8 px-8 bg-white">
    <section>
      <h2 class="py-4">
        <strong>CONDICIONES GENERALES DE CONTRATACIÓN (USUARIOS)</strong>
      </h2>
      <p><strong>Última actualización:</strong> Marzo 2026</p>

      <h2>¿Qué contienen estas condiciones?</h2>
      <p>
        Estas condiciones generales establecen las normas de uso de nuestro
        Sitio y Servicios, así como los derechos y obligaciones de Fortiasoft SL
        ("MEDKUS" o "Nosotros") y el Usuario.
      </p>
      <p>En estas condiciones, cuando decimos:</p>
      <ul>
        <li>
          <strong>"Servicios de la Salud"</strong>: nos referimos a todos los
          servicios relacionados con la salud, incluyendo diagnósticos,
          tratamientos, videoconsultas y cualesquiera otros servicios prestados
          por Profesionales. Esto nunca incluye Nuestros Servicios.
        </li>
        <li>
          <strong>"Nuestros Servicios"</strong>: nos referimos a los servicios
          que proporcionamos a través de nuestro Sitio a usuarios individuales,
          consumidores y pacientes, tal como se detalla en la Parte 2.
        </li>
        <li>
          <strong>"Profesional"</strong>: nos referimos a profesionales de la
          salud como terapeutas, médicos integrativos, nutricionistas,
          dentistas, fisioterapeutas, psicólogos y otros profesionales de la
          salud que aparecen en nuestro Sitio. Dichos profesionales pueden
          trabajar de forma independiente o para un Centro, pero en ningún caso
          están contratados por Nosotros ni trabajan para Nosotros.
        </li>
        <li>
          <strong>"Live Group"</strong>: nos referimos a los grupos temáticos
          públicos organizados por área de salud o patología, disponibles en
          nuestro Sitio, donde usuarios y profesionales pueden crear y
          participar en sesiones.
        </li>
        <li>
          <strong>"Sitio"</strong>: nos referimos a nuestra plataforma alojada
          en market.medkus.com y medkus.com, cualquier subdominio y cualquier
          otra plataforma, sitio web o aplicación móvil operada por Nosotros.
        </li>
        <li>
          <strong>"Usuario" o "Tú"</strong>: nos referimos a cualquier persona
          que en su condición de individuo, consumidor o paciente utilice
          nuestro Sitio o navegue por él, incluyendo a quienes leen opiniones,
          conciertan citas, buscan Profesionales o participan en Live Groups.
        </li>
      </ul>
      <p>
        El uso de nuestro Sitio en cualquier forma, incluida la navegación, se
        regirá por estas condiciones. Al acceder y utilizar nuestro Sitio, el
        Usuario reconoce y acepta estas condiciones. Si no estás de acuerdo con
        ellas, deberás abstenerte de utilizar nuestro Sitio.
      </p>

      <h2>PARTE 1 — CONDICIONES GENERALES</h2>

      <h3>1. Quiénes somos</h3>
      <p>
        Este Sitio está gestionado por Fortiasoft SL, con nombre comercial
        MEDKUS. Estamos registrados en España con CIF B88412986, en el Registro
        Mercantil de Madrid, y nuestro domicilio social se encuentra en Calle de
        Huelva, 16, Loft 69, 28100 Alcobendas, Madrid (España).
      </p>

      <h3>2. Capacidad para utilizar nuestro Sitio</h3>
      <p>
        Para hacer uso de Nuestros Servicios es requisito indispensable haber
        cumplido dieciséis (16) años y disponer de la capacidad legal necesaria
        para suscribir un contrato con efectos vinculantes.
      </p>
      <p>
        Queda expresamente prohibido el acceso y utilización de Nuestros
        Servicios por parte de menores de dieciocho (18) años sin sus tutores.
        En aquellos supuestos en los que una persona menor de dieciocho (18)
        años precise asistencia de un Profesional, será imprescindible que un
        adulto con plena capacidad legal contrate los servicios en
        representación del menor.
      </p>

      <h3>3. Otros aspectos aplicables</h3>
      <p>
        Estas condiciones remiten a otras políticas adicionales relevantes que
        debes revisar:
      </p>
      <ul>
        <li>
          <strong>Política de Privacidad:</strong> detalla cómo gestionamos tus
          datos personales, los motivos del tratamiento, con quién los
          compartimos y cuáles son tus derechos. Los Profesionales gestionan tus
          datos de forma independiente conforme a su propia política de
          privacidad.
        </li>
        <li>
          <strong>Política de Cookies:</strong> detalla qué cookies utilizamos,
          con qué finalidad y cómo puedes gestionarlas.
        </li>
        <li>
          <strong>Criterios de clasificación (ranking):</strong> el orden en que
          aparecen los Profesionales en los resultados de búsqueda se determina
          mediante un algoritmo que tiene en cuenta la disponibilidad del
          Profesional, la cantidad y calidad de las opiniones, el número de
          pacientes habituales y la completitud del perfil.
        </li>
        <li>
          <strong>Normas sobre opiniones:</strong> las valoraciones se realizan
          con un sistema de puntuación de estrellas de 1 a 5. El contenido de
          las opiniones debe ser respetuoso, objetivo y centrado en hechos
          ocurridos en el servicio prestado, sin incluir descalificaciones ni
          insultos hacia el profesional.
        </li>
      </ul>

      <h3>4. Acceso al Sitio y a Nuestros Servicios</h3>
      <p>
        Al utilizar nuestros Servicios, la información que facilites debe ser
        veraz y completa. En determinadas circunstancias será necesario que
        crees una cuenta; en ese caso, te comprometes a no registrar perfiles
        ficticios ni proporcionar datos falsos o no autorizados.
      </p>
      <p>
        Nos reservamos la facultad de comprobar tu identidad y podremos
        suspender, temporal o definitivamente, tu acceso si detectamos
        información falsa, engañosa o no autorizada.
      </p>
      <p>
        Para proteger la seguridad de tu cuenta, deberás establecer una
        contraseña exclusiva al crearla. Es tu deber mantener la
        confidencialidad de tus credenciales. Si sospechas que tu contraseña ha
        podido verse comprometida, cámbiala de inmediato y contacta con
        nosotros.
      </p>

      <h4>Modalidades de pago</h4>
      <p>
        El Usuario puede abonar los servicios del Profesional de dos formas:
      </p>
      <ul>
        <li>
          <strong>A través de la pasarela de MEDKUS:</strong> cuando el
          Profesional tenga habilitada esta opción, el pago se realiza con
          tarjeta de débito o crédito a través de un servidor seguro gestionado
          por un proveedor de pagos externo. MEDKUS actúa únicamente como canal
          de pago; la factura del servicio será emitida siempre por el
          Profesional. MEDKUS no almacena datos de pago. Los recibos se envían
          por correo electrónico y cada pago recibe un identificador único. Si
          detectas un cargo erróneo, contacta con nosotros a través de
          <a href="mailto:soporte@medkus.com">soporte@medkus.com</a> y lo
          resolveremos de inmediato.
        </li>
        <li>
          <strong>A través de la pasarela del Profesional:</strong> cuando el
          Profesional utilice su propia plataforma de pago, se aplicarán las
          condiciones de uso de dicha plataforma. MEDKUS no asume
          responsabilidad alguna por el funcionamiento de pasarelas de pago
          ajenas.
        </li>
      </ul>

      <h4>Derecho de desistimiento</h4>
      <p>
        Con carácter general, dispones de un plazo de 14 días naturales para
        ejercer tu derecho de desistimiento desde la celebración del contrato,
        conforme al art. 102 del Real Decreto Legislativo 1/2007 (LGDCU).
      </p>
      <p>
        No obstante, en el caso de reservas online o servicios prepagados cuya
        ejecución haya comenzado con tu consentimiento expreso, conforme al art.
        103.a) de la LGDCU, aceptas y eres consciente de que, una vez ejecutado
        el contrato, pierdes el derecho de desistimiento y no podrás solicitar
        el reembolso del importe abonado.
      </p>

      <h3>5. Prohibiciones en nuestro Sitio</h3>
      <p>Te comprometes a no:</p>
      <ul>
        <li>
          Infringir estas condiciones, cualquiera de nuestras políticas o
          cualquier ley o normativa aplicable.
        </li>
        <li>
          Utilizar nuestro Sitio de forma fraudulenta o engañosa, incluyendo la
          participación en opiniones falsas o citas fraudulentas.
        </li>
        <li>
          Publicar contenido que vaya en contra de nuestras normas de contenido.
        </li>
        <li>
          Incurrir en conductas que puedan dañar significativamente la
          reputación de MEDKUS o de otros, incluyendo comportamientos
          difamatorios, amenazadores, odiosos o abusivos.
        </li>
        <li>
          Participar en conductas que puedan atentar contra la integridad física
          o moral de otra persona o causarle angustia psicológica.
        </li>
        <li>
          Utilizar nuestro Sitio de forma que pueda dañar, inutilizar o
          perjudicar el funcionamiento o la seguridad de Nuestros Servicios.
        </li>
        <li>
          Copiar, adaptar, modificar, reproducir, desensamblar, descompilar o
          realizar ingeniería inversa sobre cualquier parte de nuestro Sitio.
        </li>
        <li>
          Obstaculizar el acceso de otros Usuarios mediante uso excesivo del
          Sitio.
        </li>
        <li>Enviar reiteradamente reclamaciones manifiestamente infundadas.</li>
        <li>
          Intentar obtener acceso no autorizado a cualquier cuenta, datos o
          sistemas informáticos.
        </li>
      </ul>

      <h3>6. Normas sobre contenidos</h3>
      <p>Está prohibido publicar:</p>
      <ul>
        <li>
          Materiales delictivos, violentos, pornográficos, amenazantes,
          difamatorios, racistas, xenófobos, homófobos, agresivos o acosadores.
        </li>
        <li>Contenido sexual o indecente.</li>
        <li>Mensajes que apoyen el terrorismo.</li>
        <li>Información contraria a la ley o al orden público.</li>
        <li>Contenidos falsos, engañosos o que suplanten identidades.</li>
        <li>
          Materiales que vulneren derechos de terceros, incluyendo derechos de
          propiedad intelectual o el derecho a la intimidad.
        </li>
        <li>
          Cualquier otro contenido que, según el criterio razonable de MEDKUS,
          pueda considerarse obsceno, contrario a la normativa o potencialmente
          dañino para la reputación de MEDKUS o de terceros.
        </li>
      </ul>
      <p>
        Si consideras que algún contenido de nuestro Sitio es ilegal, puedes
        notificárnoslo escribiendo a
        <a href="mailto:soporte@medkus.com">soporte@medkus.com</a>.
      </p>

      <h3>7. Contactar con MEDKUS</h3>
      <p>
        Hemos designado un único punto de contacto conforme al Reglamento de
        Servicios Digitales (DSA):
      </p>
      <ul>
        <li>
          <strong>Para Usuarios:</strong>
          <a href="mailto:soporte@medkus.com">soporte@medkus.com</a>
        </li>
        <li>
          <strong>Para autoridades:</strong>
          <a href="mailto:soporte@medkus.com">soporte@medkus.com</a> (idiomas:
          español e inglés)
        </li>
        <li>
          <strong>Para ejercicio de derechos de protección de datos:</strong>
          <a href="mailto:soporte@medkus.com">soporte@medkus.com</a>
        </li>
      </ul>

      <h3>8. Live Groups</h3>

      <h4>8.1. Qué son los Live Groups</h4>
      <p>
        Los Live Groups son espacios temáticos públicos organizados por área de
        salud o patología donde cualquier persona puede ver los grupos
        existentes y las sesiones creadas dentro de cada grupo. Para crear un
        grupo o una sesión es necesario estar registrado en MEDKUS.
      </p>

      <h4>8.2. Visibilidad pública</h4>
      <p>
        Los Live Groups y las sesiones creadas dentro de ellos son visibles
        públicamente para cualquier visitante del Sitio, incluso sin registro.
        Esto incluye el nombre visible del anfitrión de cada sesión.
      </p>

      <h4>8.3. Nombre visible al crear una sesión</h4>
      <p>
        Al crear una sesión, se te solicitará que elijas un nombre visible que
        aparecerá públicamente asociado a esa sesión. Puedes usar tu nombre
        real, un alias o cualquier identificador que elijas. Este nombre será
        visible para cualquier visitante del Sitio sin necesidad de registro.
      </p>
      <p>
        <strong>⚠️ Importante:</strong> dado que los Live Groups están
        organizados por áreas de salud o patología, el nombre visible que elijas
        puede quedar públicamente asociado a esa temática. Te recomendamos que
        elijas conscientemente qué nombre quieres que aparezca. MEDKUS no asume
        responsabilidad por la información que decidas hacer pública a través de
        tu nombre visible.
      </p>

      <h4>8.4. Contenido de las sesiones</h4>
      <p>
        El contenido publicado en las sesiones debe cumplir con las normas de
        contenido establecidas en la sección 6 de estas condiciones. MEDKUS se
        reserva el derecho de eliminar sesiones o grupos que incumplan dichas
        normas o que puedan resultar perjudiciales para los usuarios o la
        reputación de MEDKUS.
      </p>

      <h4>8.5. Responsabilidad del anfitrión</h4>
      <p>
        El usuario que crea una sesión es el único responsable del contenido que
        publique en ella. MEDKUS actúa como plataforma tecnológica y no
        supervisa ni valida el contenido de las sesiones en tiempo real.
      </p>

      <h3>9. Derechos de propiedad intelectual</h3>
      <p>
        Salvo indicación expresa en contrario, ostentamos la titularidad o la
        licencia de todos los derechos de propiedad intelectual sobre nuestro
        Sitio, sus contenidos, funcionalidades y los materiales publicados en
        él. Dichos derechos se encuentran protegidos por la normativa y tratados
        internacionales aplicables.
      </p>
      <p>
        Sin autorización expresa y por escrito, el Usuario no podrá reproducir,
        copiar, modificar, distribuir, descompilar ni crear obras derivadas de
        ninguna parte de nuestro Sitio, ni utilizarlo para fines distintos de la
        recepción de Nuestros Servicios.
      </p>
      <p>
        El Usuario se compromete a indemnizar y mantener indemne a MEDKUS frente
        a cualquier pérdida, daño o reclamación derivada del incumplimiento de
        esta sección. MEDKUS no reivindica derechos sobre marcas o contenidos de
        terceros que aparezcan en el Sitio, los cuales permanecen bajo la
        titularidad de sus respectivos propietarios.
      </p>

      <h3>10. Publicidad</h3>
      <p>
        Podemos mostrar anuncios digitales en nuestro Sitio, incluyendo en
        algunos casos publicidad personalizada basada en tus preferencias de
        navegación. En ningún caso compartimos datos de salud con terceros con
        fines publicitarios ni permitimos anuncios basados en perfiles de salud
        u otras categorías especiales de datos.
      </p>
      <p>Para más información consulta nuestra Política de Privacidad.</p>

      <h3>11. Confidencialidad</h3>
      <p>
        Al utilizar nuestro Sitio puedes compartir o tener conocimiento de
        información confidencial. Tanto tú como Nosotros nos comprometemos a
        tomar medidas razonables para protegerla y evitar accesos no
        autorizados. La información que pongas a disposición del público en el
        Sitio no se considera confidencial. Podemos compartir información
        confidencial con las autoridades competentes si estamos obligados a
        ello.
      </p>

      <h3>12. Seguridad de nuestros Usuarios</h3>
      <p>
        Nos tomamos muy en serio la seguridad de nuestros Usuarios. Si
        consideras que tu vida, integridad o seguridad o las de otra persona
        están en peligro, contacta con nosotros a través de
        <a href="mailto:soporte@medkus.com">soporte@medkus.com</a>. Analizaremos
        tu preocupación con carácter prioritario.
      </p>
      <p>
        Si sospechamos que se ha producido o se está produciendo un delito que
        pueda suponer una amenaza para la vida o la seguridad de un Usuario,
        suspenderemos o cancelaremos cualquier cuenta sospechosa y nos
        reservamos el derecho a informar a las autoridades competentes.
      </p>

      <h3>13. Incumplimiento y medidas contra el uso indebido</h3>
      <p>
        En caso de infracción de estas condiciones o de la ley, podremos adoptar
        medidas como:
      </p>
      <ul>
        <li>suspender o cancelar la cuenta del Usuario,</li>
        <li>limitar el uso de Nuestros Servicios,</li>
        <li>eliminar o degradar contenido generado por el Usuario,</li>
        <li>
          compartir información con las autoridades cuando sea necesario, o
        </li>
        <li>
          suspender la capacidad del Usuario para efectuar pagos a través de
          nuestro Sitio.
        </li>
      </ul>
      <p>
        El Usuario se compromete a indemnizar y mantener indemne a MEDKUS frente
        a cualquier pérdida, daño, coste o reclamación derivada de:
      </p>
      <ul>
        <li>el incumplimiento de estos términos;</li>
        <li>el uso ilegal, fraudulento o no conforme del Sitio;</li>
        <li>la aportación de datos falsos o engañosos; o</li>
        <li>la infracción de derechos de propiedad intelectual de terceros.</li>
      </ul>

      <h3>14. Procedimiento interno de reclamación</h3>
      <p>
        Si no estás conforme con alguna decisión que hayamos tomado respecto a
        contenido o acceso a nuestros Servicios, tienes derecho a impugnarlo.
        Deberás enviarnos tu reclamación, sin coste alguno, en un plazo máximo
        de 6 meses desde la notificación de la decisión, a
        <a href="mailto:soporte@medkus.com">soporte@medkus.com</a>.
      </p>
      <p>
        Revisaremos tu reclamación e informaremos del resultado. Si tras nuestra
        respuesta sigues en desacuerdo, puedes utilizar los mecanismos indicados
        en la sección 20. Si presentas más de una reclamación manifiestamente
        infundada, podremos suspender tu derecho a presentar nuevas
        reclamaciones durante un periodo razonable, previa advertencia.
      </p>

      <h3>15. Carga de contenidos</h3>
      <p>
        Al publicar contenido en nuestro Sitio (opiniones, sesiones en Live
        Groups, respuestas a preguntas, etc.), nos concedes una licencia
        mundial, no exclusiva, irrevocable, libre de regalías y transferible
        para utilizar, reproducir, distribuir, preparar trabajos derivados y
        mostrar dicho contenido en relación con Nuestros Servicios.
      </p>

      <h3>16. Renuncias y limitaciones de responsabilidad</h3>
      <p>
        MEDKUS actúa únicamente como intermediario y proveedor de servicios
        técnicos, facilitando la relación entre Usuarios y Profesionales. La
        responsabilidad final de elegir al Profesional recae en el Usuario, y es
        el Profesional quien asume la responsabilidad exclusiva de prestar
        cualquier Servicio de la Salud.
      </p>
      <p>
        Todo acuerdo, reserva o contrato relacionado con la prestación de
        Servicios de la Salud se realiza directamente entre el Usuario y el
        Profesional. MEDKUS no forma parte de estos acuerdos.
      </p>
      <p>
        Cualquier reclamación vinculada a un Profesional o a los Servicios de la
        Salud recibidos debe dirigirse directamente a dicho Profesional. MEDKUS
        no será responsable de:
      </p>
      <ul>
        <li>
          Recomendaciones, consejos, diagnósticos o información proporcionada
          por un Profesional.
        </li>
        <li>
          Negligencia, imprudencia o falta de experiencia de los Profesionales.
        </li>
        <li>
          Comunicaciones erróneas o engañosas proporcionadas por los
          Profesionales o su personal.
        </li>
        <li>
          Pagos realizados por el Usuario a los Profesionales como remuneración
          por sus servicios.
        </li>
        <li>Contenido de sitios web enlazados en nuestro Sitio.</li>
        <li>
          Contenido o información facilitada por Usuarios, Profesionales o
          Clínicas a través del Sitio.
        </li>
        <li>
          Interrupciones o fallos del Sitio atribuibles al equipo del Usuario, a
          la infraestructura de Internet o a terceros fuera de nuestro control.
        </li>
        <li>
          Virus u otros materiales dañinos que puedan afectar al equipo del
          Usuario por el uso del Sitio.
        </li>
      </ul>
      <p>
        El Sitio y los Servicios se ofrecen "tal cual" y "según disponibilidad".
        MEDKUS no garantiza que el Sitio sea seguro o esté libre de errores. El
        Usuario es responsable de configurar su tecnología y utilizar su propio
        software de protección.
      </p>

      <h3>17. Responsabilidad máxima</h3>
      <p>
        MEDKUS solo será responsable de las pérdidas directas y probadas que el
        Usuario sufra como resultado directo de un incumplimiento por nuestra
        parte de estas condiciones o de la legislación aplicable.
      </p>
      <p>
        El importe máximo acumulado que MEDKUS deberá al Usuario en virtud de
        estas condiciones estará limitado al importe abonado por el Usuario a
        MEDKUS durante los seis (6) meses anteriores al hecho que origine la
        reclamación, o a 100€ si el Usuario no ha realizado ningún pago a
        MEDKUS.
      </p>
      <p>
        En ningún caso MEDKUS, sus afiliados, empleados o directores serán
        responsables por lucro cesante, pérdida de ingresos, ni por daños
        consecuentes, incidentales, indirectos, especiales o punitivos. Lo
        anterior no excluye ni limita nuestra responsabilidad en los casos en
        que sea ilegal hacerlo conforme a la legislación aplicable.
      </p>

      <h3>18. Modificación de las condiciones</h3>
      <p>
        Cuando realicemos cambios importantes en estas condiciones, publicaremos
        las condiciones actualizadas en nuestro Sitio y actualizaremos la fecha
        de "Última actualización". Cuando así lo exija la legislación aplicable,
        también te enviaremos un aviso informándote de los cambios.
      </p>

      <h3>19. Suspensión o retirada del Sitio</h3>
      <p>
        No garantizamos que nuestro Sitio esté siempre disponible o no sufra
        interrupciones. Nos reservamos el derecho a suspender, retirar o
        restringir la disponibilidad de todo o parte de nuestro Sitio por
        motivos comerciales, de seguridad o de mantenimiento. Intentaremos
        avisarte con una antelación razonable.
      </p>

      <h3>20. Información sobre usuarios activos mensuales</h3>
      <p>
        A efectos del Reglamento de Servicios Digitales (DSA), el número
        aproximado de usuarios activos mensuales en la Unión Europea se publica
        de forma permanente al pie de página del Sitio y se actualiza cada 6
        meses.
      </p>

      <h3>21. Derecho aplicable y resolución de litigios</h3>
      <p>
        Estas condiciones se rigen e interpretan de acuerdo con la ley española.
        Puedes tramitar cualquier reclamación directamente con nosotros
        contactándonos en
        <a href="mailto:soporte@medkus.com">soporte@medkus.com</a>.
      </p>
      <p>
        Si consideras que tu reclamación no ha sido resuelta a través de
        nuestros procedimientos internos, puedes acudir a los juzgados y
        tribunales de la ciudad de Madrid, o iniciar un procedimiento de
        resolución de conflictos en línea en:
      </p>
      <p>
        <a
          href="https://ec.europa.eu/consumers/odr/main/index.cfm?event=main.home2.show&amp;lng=ES"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://ec.europa.eu/consumers/odr/main/index.cfm?event=main.home2.show&amp;lng=ES
        </a>
      </p>

      <h3>22. Acontecimientos fuera de nuestro control</h3>
      <p>
        No seremos responsables por cualquier fallo del Sitio o indisponibilidad
        de Nuestros Servicios que surja de circunstancias o eventos fuera de
        nuestro control razonable.
      </p>

      <h2>PARTE 2 — CONDICIONES ESPECÍFICAS</h2>

      <h3>1. Nuestros Servicios</h3>
      <p>MEDKUS ofrece los siguientes servicios a través del Sitio:</p>
      <ul>
        <li>
          <strong>Directorio de Profesionales:</strong> acceso a un directorio
          de búsqueda con perfiles de Profesionales y Clínicas.
        </li>
        <li>
          <strong>Reserva de citas:</strong> posibilidad de reservar y gestionar
          citas con Profesionales o Centros.
        </li>
        <li>
          <strong>Opiniones:</strong> posibilidad de escribir y consultar
          opiniones sobre Profesionales y Clínicas, conforme a las normas de
          contenido establecidas en estas condiciones.
        </li>
        <li>
          <strong>Live Groups:</strong> posibilidad de consultar grupos y
          sesiones públicas organizados por área de salud, y de crear grupos y
          sesiones una vez registrado.
        </li>
      </ul>
      <p>
        <strong
          >⚠️ Nuestros Servicios no están pensados para emergencias
          sanitarias.</strong
        >
        Si tienes una urgencia, llama al 112 o al 061, o dirígete al servicio de
        urgencias más cercano.
      </p>

      <h3>2. Concertar una cita con un Profesional</h3>
      <p>
        Es posible reservar una cita con cualquier Profesional que disponga de
        horarios disponibles. Para completar la reserva, si aún no tienes
        cuenta, se te solicitará crear una.
      </p>
      <p>
        La información sobre los Profesionales proviene de registros públicos o
        ha sido proporcionada por los propios Profesionales. Solicitamos a los
        Profesionales que nos entreguen datos precisos y cuenten con las
        licencias vigentes requeridas para ejercer su especialidad.
      </p>
      <p>
        No obstante, MEDKUS actúa como intermediario y no garantiza las
        cualificaciones, experiencia, idoneidad, calidad, horarios ni
        información sobre precios de los Profesionales.
      </p>
      <p>
        La información disponible en nuestro Sitio es meramente informativa y no
        debe interpretarse como consejo médico ni sustituir el asesoramiento
        profesional sanitario.
      </p>
      <p>
        El Usuario es responsable de los honorarios y costes derivados de la
        relación con el Profesional.
      </p>
      <p>
        Si tras reservar una cita no asistes o la cancelas, podrías estar sujeto
        a cargos o penalizaciones según las condiciones del Profesional. Te
        recomendamos revisar su política de cancelación antes de reservar.
      </p>
      <p>
        MEDKUS no asume responsabilidad en caso de cancelación por parte del
        Profesional tras la reserva realizada por el paciente.
      </p>

      <h3>3. Reservas en nombre de un familiar</h3>
      <p>
        El Usuario puede reservar citas en nombre de un familiar. En tal caso,
        declara y garantiza que cuenta con la autorización legal o expresa del
        familiar para:
      </p>
      <ul>
        <li>utilizar y registrar sus datos personales en el Sitio;</li>
        <li>reservar, modificar o cancelar citas en su beneficio;</li>
        <li>
          transmitir y recibir información de los Profesionales en relación con
          el familiar; y
        </li>
        <li>enviar y recibir documentos relacionados con su salud.</li>
      </ul>
      <p>
        El Usuario acepta indemnizar y eximir de responsabilidad a MEDKUS y a
        los Profesionales por cualquier daño derivado del incumplimiento de
        estas condiciones o de la incapacidad para representar válidamente al
        familiar.
      </p>
    </section>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import { useSeo } from "@/composables/useSeo";

export default defineComponent({
  name: "GeneralConditions",
  components: {},
  setup() {
    const { t } = useI18n();
    useSeo({
      title: t("seoGeneralConditionsTitle"),
      description: t("seoGeneralConditionsDesc"),
      noindex: true,
    });
  },
});
</script>
